// Long-lived client to the auth-server's daemon-facing endpoints (regime B /
// account-bound pairing).
//
// What it does:
//   - Polls GET /daemon/allowlist every ~30s (configurable) for the current
//     allow-list of trusted device pubkeys for this daemon's owning user.
//   - Verifies the Ed25519 signature on the response against the pubkey we
//     pinned at /daemon/link time. Rejects unsigned / forged updates.
//   - Maintains an in-memory `Set<string>` of trusted pubkeys that downstream
//     authorization checks consult on every incoming connection.
//
// What it deliberately doesn't do:
//   - WebSocket / push. v0.8 uses polling for simplicity. Revocation is
//     bounded by the poll interval (~30s in the worst case) — fine for
//     personal-use scale; revisit when we have multi-tenant load.
//   - Block any operation. If the auth-server is unreachable, the daemon
//     falls back to its last-known allow-list. Daemons paired before the
//     auth-server was a thing keep working through their original QR-time
//     trust path (regime A) — see websocket-server.ts for the OR gate.
//
// Lifecycle:
//   - Constructed once in bootstrap.ts when persisted config has an `account`
//     block (i.e. this daemon has been linked).
//   - `start()` kicks off the poll loop and returns immediately.
//   - `stop()` cancels and clears state. Idempotent.

import { type Logger } from "pino";
import {
  canonicalAllowlistMessage,
  verifyAuthServerSignature,
} from "./auth-server-signature-verify.js";

// ============================================================================
// Auth-server todo HTTP client — daemon-facing /daemon/todos* endpoints.
// All calls use the same daemonToken + authServerUrl as the allowlist client.
// ============================================================================

export interface AuthServerSubtodo {
  id: string;
  todoId: string;
  text: string;
  position: number;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  notes: string;
  status: string;
  scheduledAt: string | null;
  dueAt: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  createdByUserImage: string | null;
  createdByAgentId: string | null;
  createdByHostId: string | null;
  assignedToUserId: string | null;
  assignedToUserName: string | null;
  assignedToUserImage: string | null;
  priority: number;
  scheduleTargetHostId: string | null;
  blockedBySubtodoId: string | null;
}

export interface AuthServerTodo {
  id: string;
  repoKey: string;
  text: string;
  notes: string;
  position: number;
  completedAt: string | null;
  linkedAgentHostId: string | null;
  linkedAgentId: string | null;
  createdByAgentId: string | null;
  createdByCwd: string | null;
  createdByUserId: string | null;
  createdByUserName: string | null;
  createdByUserImage: string | null;
  createdByHostId: string | null;
  assignedToUserId: string | null;
  assignedToUserName: string | null;
  assignedToUserImage: string | null;
  createdAt: string;
  updatedAt: string;
  subtodos: AuthServerSubtodo[];
  status: string;
  priority: number;
  scheduledAt: string | null;
  dueAt: string | null;
  scheduleTargetHostId: string | null;
  blockedByTodoId: string | null;
  resultReport: string | null;
}

export interface AuthServerTodoClient {
  listTodos(repoKey: string): Promise<{ todos: AuthServerTodo[] }>;
  createTodo(input: {
    repoKey: string;
    text: string;
    notes?: string;
    position?: number;
    createdByAgentId?: string | null;
    createdByCwd?: string | null;
    createdByHostId?: string | null;
    linkedAgentHostId?: string | null;
    linkedAgentId?: string | null;
  }): Promise<AuthServerTodo>;
  updateTodo(
    id: string,
    patch: {
      text?: string;
      notes?: string;
      position?: number;
      completedAt?: string | null;
      linkedAgentHostId?: string | null;
      linkedAgentId?: string | null;
      assignedToUserId?: string | null;
      status?: string;
      priority?: number;
      scheduledAt?: string | null;
      dueAt?: string | null;
      scheduleTargetHostId?: string | null;
      blockedByTodoId?: string | null;
      resultReport?: string | null;
      /** When provided, also inserts a todo_sessions row + session_linked activity. */
      linkedAgentHostIdForActivity?: string | null;
      linkedAgentIdForActivity?: string | null;
      /** Actor attribution for daemon-initiated writes (scheduler, complete_todo). */
      actorAgentId?: string | null;
      actorHostId?: string | null;
    },
  ): Promise<AuthServerTodo>;
  deleteTodo(id: string): Promise<void>;
  reorderTodos(repoKey: string, orderedIds: string[]): Promise<void>;
  createSubtodo(
    todoId: string,
    input: { text: string; position?: number },
  ): Promise<AuthServerSubtodo>;
  updateSubtodo(
    id: string,
    patch: {
      text?: string;
      position?: number;
      completedAt?: string | null;
      notes?: string;
      status?: string;
      scheduledAt?: string | null;
      dueAt?: string | null;
      assignedToUserId?: string | null;
      priority?: number;
      scheduleTargetHostId?: string | null;
      blockedBySubtodoId?: string | null;
    },
  ): Promise<AuthServerSubtodo>;
  deleteSubtodo(id: string): Promise<void>;
  reorderSubtodos(todoId: string, orderedIds: string[]): Promise<void>;
  listTodoActivity(todoId: string): Promise<{ activity: AuthServerActivityEntry[] }>;
  listTodoSessions(todoId: string): Promise<{ sessions: AuthServerTodoSession[] }>;
  addTodoComment(
    todoId: string,
    body: string,
    opts?: { actorAgentId?: string | null; actorHostId?: string | null },
  ): Promise<AuthServerActivityEntry>;
  getDueTodos(): Promise<{ todos: AuthServerTodo[] }>;
}

export interface AuthServerTodoSession {
  id: string;
  todoId: string;
  hostId: string;
  agentId: string;
  linkedAt: string;
  unlinkedAt: string | null;
}

export interface AuthServerActivityEntry {
  id: string;
  todoId: string;
  subtodoId: string | null;
  kind: string;
  actorUserId: string | null;
  actorUserName: string | null;
  actorUserImage: string | null;
  actorAgentId: string | null;
  actorHostId: string | null;
  body: string | null;
  createdAt: string;
}

const TODO_REQUEST_TIMEOUT_MS = 10_000;

export function createAuthServerTodoClient(options: {
  logger: Logger;
  authServerUrl: string;
  daemonToken: string;
}): AuthServerTodoClient {
  const { authServerUrl, daemonToken } = options;
  const log = options.logger.child({ component: "auth-server-todo-client" });

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const ac = new AbortController();
    const timeoutHandle = setTimeout(() => ac.abort(), TODO_REQUEST_TIMEOUT_MS);
    try {
      const url = new URL(path, authServerUrl).toString();
      const res = await fetch(url, {
        method,
        headers: {
          authorization: `Bearer ${daemonToken}`,
          ...(body !== undefined ? { "content-type": "application/json" } : {}),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: ac.signal,
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "(unreadable)");
        throw new Error(`auth-server todo request ${method} ${path} failed: ${res.status} ${text}`);
      }
      if (res.status === 204) {
        return undefined as T;
      }
      return (await res.json()) as T;
    } catch (err) {
      log.warn({ err, method, path }, "auth-server todo request error");
      throw err;
    } finally {
      clearTimeout(timeoutHandle);
    }
  }

  return {
    async listTodos(repoKey) {
      const params = new URLSearchParams({ repoKey });
      return request<{ todos: AuthServerTodo[] }>("GET", `/daemon/todos?${params.toString()}`);
    },
    async createTodo(input) {
      const { todo } = await request<{ todo: AuthServerTodo }>("POST", "/daemon/todos", {
        repoKey: input.repoKey,
        text: input.text,
        notes: input.notes ?? "",
        position: input.position ?? 0,
        createdByAgentId: input.createdByAgentId ?? null,
        createdByCwd: input.createdByCwd ?? null,
        createdByHostId: input.createdByHostId ?? null,
        linkedAgentHostId: input.linkedAgentHostId ?? null,
        linkedAgentId: input.linkedAgentId ?? null,
      });
      return todo;
    },
    async updateTodo(id, patch) {
      const { todo } = await request<{ todo: AuthServerTodo }>(
        "PATCH",
        `/daemon/todos/${encodeURIComponent(id)}`,
        patch,
      );
      return todo;
    },
    async deleteTodo(id) {
      await request<void>("DELETE", `/daemon/todos/${encodeURIComponent(id)}`);
    },
    async reorderTodos(repoKey, orderedIds) {
      const order = orderedIds.map((todoId, i) => ({ id: todoId, position: i }));
      await request<{ ok: boolean }>("PUT", "/daemon/todos/order", { repoKey, order });
    },
    async createSubtodo(todoId, input) {
      const { subtodo } = await request<{ subtodo: AuthServerSubtodo }>(
        "POST",
        `/daemon/todos/${encodeURIComponent(todoId)}/subtodos`,
        { text: input.text, position: input.position ?? 0 },
      );
      return subtodo;
    },
    async updateSubtodo(id, patch) {
      const { subtodo } = await request<{ subtodo: AuthServerSubtodo }>(
        "PATCH",
        `/daemon/subtodos/${encodeURIComponent(id)}`,
        patch,
      );
      return subtodo;
    },
    async deleteSubtodo(id) {
      await request<void>("DELETE", `/daemon/subtodos/${encodeURIComponent(id)}`);
    },
    async reorderSubtodos(todoId, orderedIds) {
      const order = orderedIds.map((subtodoId, i) => ({ id: subtodoId, position: i }));
      await request<{ ok: boolean }>(
        "PUT",
        `/daemon/todos/${encodeURIComponent(todoId)}/subtodos/order`,
        { order },
      );
    },
    async listTodoActivity(todoId) {
      return request<{ activity: AuthServerActivityEntry[] }>(
        "GET",
        `/daemon/todos/${encodeURIComponent(todoId)}/activity`,
      );
    },
    async listTodoSessions(todoId) {
      return request<{ sessions: AuthServerTodoSession[] }>(
        "GET",
        `/daemon/todos/${encodeURIComponent(todoId)}/sessions`,
      );
    },
    async addTodoComment(todoId, body, opts) {
      const { activity } = await request<{ activity: AuthServerActivityEntry }>(
        "POST",
        `/daemon/todos/${encodeURIComponent(todoId)}/activity`,
        { body, actorAgentId: opts?.actorAgentId ?? null, actorHostId: opts?.actorHostId ?? null },
      );
      return activity;
    },
    async getDueTodos() {
      return request<{ todos: AuthServerTodo[] }>("GET", "/daemon/todos/due");
    },
  };
}

// ============================================================================
// Auth-server tab-runtime HTTP client — daemon-facing PATCH for the tabs row.
// Replaces the deleted sessions projection. The daemon's coalesced session
// fan-out (`drainSessionFanOut` in agent-manager.ts) calls patchTabRuntime
// to stamp runtime fields (status / attention / activity) onto the canonical
// tab. Same Bearer token + fire-and-forget posture as the allowlist / todos
// clients. See packages/auth-server/src/routes/daemon-sync.ts for the route.
// ============================================================================

/**
 * Patch the runtime fields onto a tab keyed by
 * `(userId, projectId, targetKind, targetId)`. The auth-server returns 404
 * when no tab exists yet (internal agent, or tab-backfill hasn't run for
 * this agent); the client swallows it as benign — the next call will retry.
 */
export interface AuthServerTabRuntimePatch {
  userId: string;
  /** Daemon-side dotfile project UUID (NOT the auth-server `prj_...` id). */
  projectId: string;
  targetKind: "agent" | "quest" | "file" | "terminal";
  targetId: string;
  /** Agent title → tab label, so daemon-side (haiku) renames reach Recents. */
  label?: string | null;
  status?: string | null;
  attentionState?: "none" | "needs-input" | "permission-prompt" | "error" | null;
  pendingPermissionCount?: number;
  lastActivityAt?: string | null;
  /**
   * Agent provider (claude / codex / opencode / custom id). Stamped on every
   * runtime patch so legacy tabs created before the provider column existed
   * self-heal on next activity.
   */
  provider?: string | null;
}

/**
 * Outcome of a runtime patch, so the caller (agent-manager fan-out) can
 * decide whether to retry:
 * - "ok"        — patch landed (or was a benign no-op / non-retryable 4xx).
 * - "not_found" — auth-server has no matching tab row (404). Retrying
 *                 forever is pointless (internal agents legitimately have
 *                 no tab), but a couple of retries cover the tab-backfill /
 *                 tab-create race.
 * - "retryable" — network error, timeout, or 5xx. Worth a backoff retry.
 */
export type PatchTabRuntimeResult = "ok" | "not_found" | "retryable";

export interface AuthServerSessionClient {
  /** Never throws — failures are reported via the result value. */
  patchTabRuntime(input: AuthServerTabRuntimePatch): Promise<PatchTabRuntimeResult>;
}

const SESSION_REQUEST_TIMEOUT_MS = 10_000;

export function createAuthServerSessionClient(options: {
  logger: Logger;
  authServerUrl: string;
  daemonToken: string;
}): AuthServerSessionClient {
  const { authServerUrl, daemonToken } = options;
  const log = options.logger.child({ component: "auth-server-session-client" });

  return {
    async patchTabRuntime(input) {
      const ac = new AbortController();
      const timeoutHandle = setTimeout(() => ac.abort(), SESSION_REQUEST_TIMEOUT_MS);
      try {
        const url = new URL("/daemon/sync/tabs/by-target/runtime", authServerUrl).toString();
        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            authorization: `Bearer ${daemonToken}`,
            "content-type": "application/json",
          },
          body: JSON.stringify(input),
          signal: ac.signal,
        });
        if (res.status === 404) {
          // Tab doesn't exist yet — common, not an error.
          return "not_found";
        }
        if (!res.ok) {
          const text = await res.text().catch(() => "(unreadable)");
          log.debug(
            { status: res.status, text, targetId: input.targetId },
            "patchTabRuntime: non-2xx",
          );
          // 5xx = transient auth-server trouble, worth a retry. Other 4xx
          // (400/403) is a request-shape/permission problem — retrying the
          // same payload can't help.
          return res.status >= 500 ? "retryable" : "ok";
        }
        return "ok";
      } catch (err) {
        log.debug({ err, targetId: input.targetId }, "patchTabRuntime error");
        return "retryable";
      } finally {
        clearTimeout(timeoutHandle);
      }
    },
  };
}

interface AuthServerClientOptions {
  logger: Logger;
  /** Origin of the auth-server (e.g. "https://auth.appostle.app"). */
  authServerUrl: string;
  /** Long-lived Bearer for /daemon/* — issued at link time. */
  daemonToken: string;
  /** Pinned Ed25519 public key (hex) we verify allow-list signatures against. */
  serverPubkeyHex: string;
  /** Override poll cadence (ms). Default 30s. */
  pollIntervalMs?: number;
  /**
   * Called when a userId that was present on the previous successful v2
   * allowlist poll is absent from the current one. Only fires after the
   * initial population (first poll emits nothing). Skipped entirely when the
   * v2 entries are missing from the response (older auth-server or transient
   * bad response) — avoids accidental mass-revocation.
   */
  onUserRemoved?: (userId: string, reason: "removed_from_allowlist") => void;
  directEndpoints?: () => Array<{ endpoint: string; source: string; observedAt: string }>;
}

interface AllowlistResponse {
  pubkeys: string[];
  signedAt: string;
  signature: string;
  version: string;
  // v2 additive fields — auth-server may or may not include these depending
  // on its own version. New daemons prefer entries when present (multi-tenant
  // session isolation); old daemons just ignore them.
  entries?: { pubkey: string; userId: string }[];
  signatureV2?: string;
  hostOwnerUserId?: string;
}

export interface AuthServerClient {
  /** Begin polling. Idempotent — second call is a no-op. */
  start(): void;
  /** Stop polling and clear in-memory allow-list. Idempotent. */
  stop(): void;
  /** Read the current allow-list snapshot. Empty until first successful poll. */
  getAllowlist(): ReadonlySet<string>;
  /**
   * Resolve a device pubkey to its owning userId. Returns `null` if the
   * pubkey isn't on the allow-list, or if the auth-server only sent the v1
   * response shape (no per-pubkey userId available — pre-multi-tenant
   * server). Daemons should fall back to non-isolated behavior in that case.
   */
  getUserIdForPubkey(pubkey: string): string | null;
  /** The host's owning user (from v2 response). Null if v1-only. */
  getHostOwnerUserId(): string | null;
  /** ISO timestamp of the last successful poll, or null. */
  getLastSyncedAt(): string | null;
  /** Fire one poll right now (ignored if already in flight or stopped). */
  refresh(): Promise<void>;
}

const DEFAULT_POLL_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 10_000;

export function createAuthServerClient(options: AuthServerClientOptions): AuthServerClient {
  const { logger, authServerUrl, daemonToken, serverPubkeyHex } = options;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  const log = logger.child({ component: "auth-server-client" });

  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight = false;
  let stopped = false;
  let allowlist: Set<string> = new Set();
  // Multi-tenant addition: pubkey → userId map populated when the auth-server
  // includes `entries` in the response. Empty when talking to an older
  // auth-server (callers must treat null as "no per-user info available" and
  // fall back to non-isolated behavior).
  let pubkeyToUserId: Map<string, string> = new Map();
  // Track the user set seen on the last successful v2 poll so we can diff
  // it on the next poll and fire onUserRemoved for any departures.
  // Null = no v2 poll has succeeded yet (suppresses diff on first poll).
  let previousUserIds: Set<string> | null = null;
  let hostOwnerUserId: string | null = null;
  let lastSyncedAt: string | null = null;

  async function pollOnce(): Promise<void> {
    if (stopped || inFlight) return;
    inFlight = true;
    const ac = new AbortController();
    const timeoutHandle = setTimeout(() => ac.abort(), REQUEST_TIMEOUT_MS);
    try {
      const url = new URL("/daemon/allowlist", authServerUrl).toString();
      const directEndpoints = options.directEndpoints?.() ?? [];
      const response = await fetch(url, {
        method: directEndpoints.length > 0 ? "POST" : "GET",
        headers: {
          authorization: `Bearer ${daemonToken}`,
          ...(directEndpoints.length > 0 ? { "content-type": "application/json" } : {}),
        },
        body: directEndpoints.length > 0 ? JSON.stringify({ directEndpoints }) : undefined,
        signal: ac.signal,
      });
      if (!response.ok) {
        log.warn({ status: response.status }, "allowlist fetch failed");
        return;
      }
      const body = (await response.json()) as Partial<AllowlistResponse>;

      // Schema check — be defensive against the auth-server returning
      // something unexpected. We want to fail closed (drop the response)
      // not fail open (clear the allow-list because of a parse error).
      if (
        !body ||
        !Array.isArray(body.pubkeys) ||
        typeof body.signedAt !== "string" ||
        typeof body.signature !== "string"
      ) {
        log.warn({ body }, "allowlist response malformed");
        return;
      }
      const pubkeys = body.pubkeys.filter((p): p is string => typeof p === "string");

      // Verify the signature against the pinned server pubkey. If this
      // fails, we treat the response as untrusted and keep the previous
      // allow-list — better stale than poisoned.
      const canonical = canonicalAllowlistMessage({ signedAt: body.signedAt, pubkeys });
      const sigBytes = Buffer.from(body.signature, "hex");
      const ok = verifyAuthServerSignature(canonical, sigBytes, serverPubkeyHex);
      if (!ok) {
        log.error("allowlist signature verification failed — refusing update");
        return;
      }

      const next = new Set(pubkeys);
      // Diff-log only when the set actually changes. This is the audit trail.
      if (next.size !== allowlist.size || [...next].some((p) => !allowlist.has(p))) {
        log.info({ count: next.size, signedAt: body.signedAt }, "allowlist updated");
      }
      allowlist = next;

      // v2 / multi-tenant: build the pubkey→userId map ONLY if the response
      // carries `entries` AND its independent signature verifies. We treat
      // entries as a separate trust unit from `pubkeys` so a malformed or
      // unverified v2 block can't poison the v1 path that the daemon
      // already trusted.
      const rawEntries = Array.isArray(body.entries) ? body.entries : null;
      const sigV2 = typeof body.signatureV2 === "string" ? body.signatureV2 : null;
      if (rawEntries && sigV2) {
        const cleanEntries: { pubkey: string; userId: string }[] = [];
        for (const e of rawEntries) {
          if (
            e &&
            typeof e === "object" &&
            typeof (e as { pubkey?: unknown }).pubkey === "string" &&
            typeof (e as { userId?: unknown }).userId === "string"
          ) {
            cleanEntries.push({
              pubkey: (e as { pubkey: string }).pubkey,
              userId: (e as { userId: string }).userId,
            });
          }
        }
        const canonicalV2 = [
          "v2",
          body.signedAt,
          ...cleanEntries.map((e) => `${e.pubkey}:${e.userId}`),
        ].join("\n");
        const okV2 = verifyAuthServerSignature(
          canonicalV2,
          Buffer.from(sigV2, "hex"),
          serverPubkeyHex,
        );
        if (okV2) {
          const nextMap = new Map<string, string>();
          for (const e of cleanEntries) nextMap.set(e.pubkey, e.userId);

          // Diff user set: fire onUserRemoved for any userId present in the
          // previous poll that is absent from this one. Skip on the very first
          // successful v2 poll (previousUserIds === null) — that would
          // incorrectly fire for every user as if they were all newly removed.
          if (options.onUserRemoved && previousUserIds !== null) {
            const currentUserIds = new Set(nextMap.values());
            for (const userId of previousUserIds) {
              if (!currentUserIds.has(userId)) {
                log.info({ userId }, "user removed from allowlist — firing onUserRemoved");
                try {
                  options.onUserRemoved(userId, "removed_from_allowlist");
                } catch (cbErr) {
                  log.warn({ err: cbErr, userId }, "onUserRemoved callback threw");
                }
              }
            }
          }

          previousUserIds = new Set(nextMap.values());
          pubkeyToUserId = nextMap;
          hostOwnerUserId = typeof body.hostOwnerUserId === "string" ? body.hostOwnerUserId : null;
        } else {
          log.warn("v2 allowlist signature failed — keeping previous pubkey→userId map");
        }
      } else if (pubkeyToUserId.size > 0) {
        // Auth-server downgraded to v1-only (unlikely once it's deployed,
        // but defensively clear so we don't keep stale userIds around).
        // Do NOT diff the user set here — missing v2 block means we have no
        // reliable current set, so firing onUserRemoved would be a false mass-
        // revocation. Just clear the maps silently.
        log.info("v2 allowlist fields missing — clearing pubkey→userId map");
        pubkeyToUserId = new Map();
        // Keep previousUserIds as-is so the next successful v2 response gets
        // a proper diff against the last known good state.
        hostOwnerUserId = null;
      }

      lastSyncedAt = body.signedAt;
    } catch (err) {
      log.warn({ err }, "allowlist poll error (will retry on next interval)");
    } finally {
      clearTimeout(timeoutHandle);
      inFlight = false;
    }
  }

  return {
    start() {
      if (timer || stopped) return;
      log.info({ authServerUrl, pollIntervalMs }, "auth-server client starting");
      // Immediate first poll, then on interval. Don't await — let it run.
      void pollOnce();
      timer = setInterval(() => {
        void pollOnce();
      }, pollIntervalMs);
    },
    stop() {
      stopped = true;
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
      allowlist = new Set();
      pubkeyToUserId = new Map();
      hostOwnerUserId = null;
      log.info("auth-server client stopped");
    },
    getAllowlist() {
      return allowlist;
    },
    getUserIdForPubkey(pubkey: string) {
      return pubkeyToUserId.get(pubkey) ?? null;
    },
    getHostOwnerUserId() {
      return hostOwnerUserId;
    },
    getLastSyncedAt() {
      return lastSyncedAt;
    },
    async refresh() {
      await pollOnce();
    },
  };
}
